/**
 * Career Intelligence Service
 * API client for career-related endpoints
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
const CAREER_API = `${API_BASE_URL}/api/career`;

class CareerService {
  /**
   * Get skill demand analysis
   */
  async getSkillDemand(skill, region = 'global') {
    try {
      const response = await axios.get(`${CAREER_API}/skill-demand/${skill}`, {
        params: { region }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching skill demand:', error);
      throw error;
    }
  }

  /**
   * Search for jobs across platforms
   */
  async searchJobs(query, options = {}) {
    try {
      const response = await axios.get(`${CAREER_API}/jobs/search`, {
        params: {
          query,
          location: options.location,
          remote: options.remote,
          limit: options.limit || 20
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching jobs:', error);
      throw error;
    }
  }

  /**
   * Get personalized job matches (requires authentication)
   */
  async getMatchingJobs(token, preferences = {}) {
    try {
      const response = await axios.get(`${CAREER_API}/jobs/matching`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          location: preferences.location,
          remote: preferences.remote,
          experienceLevel: preferences.experienceLevel,
          limit: preferences.limit || 20
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching matching jobs:', error);
      throw error;
    }
  }

  /**
   * Get emerging skills by industry
   */
  async getEmergingSkills(industry, timeframe = '12months', limit = 10) {
    try {
      const response = await axios.get(`${CAREER_API}/emerging-skills/${industry}`, {
        params: { timeframe, limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching emerging skills:', error);
      throw error;
    }
  }

  /**
   * Get salary forecast for a role
   */
  async getSalaryForecast(role, options = {}) {
    try {
      const response = await axios.get(
        `${CAREER_API}/salary-forecast/${encodeURIComponent(role)}`,
        {
          params: {
            location: options.location || 'global',
            experienceLevel: options.experienceLevel || 'mid'
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching salary forecast:', error);
      throw error;
    }
  }

  /**
   * Get recent job postings
   */
  async getRecentJobs(options = {}) {
    try {
      const response = await axios.get(`${CAREER_API}/jobs/recent`, {
        params: {
          limit: options.limit || 50,
          region: options.region,
          category: options.category
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      throw error;
    }
  }

  /**
   * Get market overview statistics
   */
  async getMarketStats() {
    try {
      const response = await axios.get(`${CAREER_API}/stats/overview`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching market stats:', error);
      throw error;
    }
  }
}

export default new CareerService();
